import { ReactNode } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { classNames } from "./Utils/classNames.ts";

function buildPageWindow(
  currentPage: number,
  totalPages: number,
  windowSize: number,
): number[] {
  const size = Math.min(windowSize, totalPages);
  const start = Math.min(
    Math.max(0, currentPage - Math.floor(size / 2)),
    totalPages - size,
  );
  return Array.from({ length: size }, (_, i) => start + i);
}

function PageButton({
  label,
  onClick,
  disabled,
  active,
  title,
}: {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={classNames(
        "min-w-8 h-8 px-2 rounded-md text-sm font-medium border transition-colors flex items-center justify-center",
        active
          ? "bg-primary text-white border-primary"
          : "bg-white text-gray-600 border-gray-300 hover:border-gray-500",
        disabled
          ? "opacity-40 cursor-not-allowed hover:border-gray-300"
          : undefined,
      )}
    >
      {label}
    </button>
  );
}

export function Pagination({
  currentPage,
  totalPages,
  onChangePage,
  windowSize = 7,
}: {
  currentPage: number;
  totalPages: number;
  onChangePage: (page: number) => void;
  windowSize?: number;
}) {
  if (totalPages <= 1) return null;
  const isFirstPage = currentPage <= 0;
  const isLastPage = currentPage >= totalPages - 1;

  return (
    <nav className="flex items-center justify-center gap-1">
      <PageButton
        label={<ChevronsLeft className="w-4 h-4" />}
        title="Prima pagina"
        disabled={isFirstPage}
        onClick={() => onChangePage(0)}
      />
      <PageButton
        label={<ChevronLeft className="w-4 h-4" />}
        title="Pagina precedente"
        disabled={isFirstPage}
        onClick={() => onChangePage(currentPage - 1)}
      />
      {buildPageWindow(currentPage, totalPages, windowSize).map((page) => (
        <PageButton
          key={page}
          label={page + 1}
          active={page === currentPage}
          onClick={() => onChangePage(page)}
        />
      ))}
      <PageButton
        label={<ChevronRight className="w-4 h-4" />}
        title="Pagina successiva"
        disabled={isLastPage}
        onClick={() => onChangePage(currentPage + 1)}
      />
      <PageButton
        label={<ChevronsRight className="w-4 h-4" />}
        title="Ultima pagina"
        disabled={isLastPage}
        onClick={() => onChangePage(totalPages - 1)}
      />
    </nav>
  );
}
